import { Tooltip } from 'antd'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { ServerPrivateRegionNodeSpec } from '@/api/type-server'
import InfoIcon from '@/assets/icons/info-i.svg?react'

import { SpecCategory, SpecCategoryType } from '../constants/specCategory'
import {
  AllocationDisplay,
  formatCpuAllocation,
  formatMemoryAllocation,
  formatStorageAllocation,
} from '../utils/nodeFormatters'

interface InstanceSpecSectionProps {
  spec: ServerPrivateRegionNodeSpec
  category: SpecCategoryType
  nodeTotalCpuCores?: number
  nodeTotalRamMB?: number
  nodeTotalDiskGB?: number
}

// Category label mapping
const CATEGORY_LABELS: Record<SpecCategoryType, string> = {
  [SpecCategory.VGPU]: 'vGPU',
  [SpecCategory.GPU]: 'GPU',
  [SpecCategory.CPU]: 'vCPU',
}

// Allocation value display component
function AllocationValue({ allocation }: { allocation: AllocationDisplay }) {
  return (
    <div className="t-sb text-primary font-bold">
      {allocation.value}
      {allocation.subValue && <span className="text-tertiary ml-1">{allocation.subValue}</span>}
    </div>
  )
}

// Field with optional tooltip
function FieldWithTooltip({
  label,
  tooltipKey,
  children,
}: {
  label: string
  tooltipKey?: string
  children: React.ReactNode
}) {
  const { t } = useTranslation('privateRegion')
  return (
    <div className="flex-1 min-w-[226px] flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <span className="t-xs text-secondary font-semibold">{label}</span>
        {tooltipKey && (
          <Tooltip
            title={t(tooltipKey)}
            classNames={{
              root: 't-xs max-w-[180px] rounded-md',
            }}
            styles={{
              body: { textAlign: 'center' },
            }}
          >
            <InfoIcon className="t-xs text-secondary" />
          </Tooltip>
        )}
      </div>
      {children}
    </div>
  )
}

// Simple field without tooltip
function SimpleField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex-1 min-w-[226px] flex flex-col gap-1">
      <span className="t-xs text-secondary font-semibold">{label}</span>
      {children}
    </div>
  )
}

// Empty placeholder to maintain grid alignment
function EmptyField() {
  return <div className="flex-1 min-w-[226px]" />
}

export const InstanceSpecSection = memo(function InstanceSpecSection({
  spec,
  category,
  nodeTotalCpuCores,
  nodeTotalRamMB,
  nodeTotalDiskGB,
}: InstanceSpecSectionProps) {
  const { t } = useTranslation('privateRegion')

  const cpuAlloc = formatCpuAllocation(spec.cpuCores, nodeTotalCpuCores ?? spec.totalCpuCores)
  const memAlloc = formatMemoryAllocation(spec.ramMB, nodeTotalRamMB ?? spec.totalRamMB)
  const storageAlloc = formatStorageAllocation(spec.diskGB, nodeTotalDiskGB ?? spec.totalDiskGB)

  const label = CATEGORY_LABELS[category]
  const isVGPU = category === SpecCategory.VGPU
  const isGPU = category === SpecCategory.GPU
  const isCPU = category === SpecCategory.CPU
  const disabled = !spec.enabled

  return (
    <div className="flex flex-col gap-4">
      {/* Section Divider */}
      <div className="flex gap-2 items-center w-full">
        <span className="t-xs text-tertiary font-semibold">{label}</span>
        <div className="flex-1 h-px bg-neutral-200" />
      </div>

      {/* Row 1: Spec Unit, GPU Number (vGPU/GPU), placeholders */}
      <div className="flex gap-4">
        <FieldWithTooltip
          label={t('instanceSpec.specUnit')}
          tooltipKey="instanceSpec.specUnitTooltip"
        >
          <div className="t-sb text-primary font-bold">{disabled ? '-' : spec.units}</div>
        </FieldWithTooltip>

        {(isVGPU || isGPU) && (
          <FieldWithTooltip
            label={t(isVGPU ? 'instanceSpec.vgpuNumber' : 'instanceSpec.gpuNumber')}
            tooltipKey={isVGPU ? 'instanceSpec.vgpuNumberTooltip' : 'instanceSpec.gpuNumberTooltip'}
          >
            <div className="t-sb text-primary font-bold">{disabled ? '-' : spec.gpuNum}</div>
          </FieldWithTooltip>
        )}

        {isVGPU && (
          <FieldWithTooltip
            label={t('instanceSpec.vgpuGpc')}
            tooltipKey="instanceSpec.vgpuGpcTooltip"
          >
            <div className="t-sb text-primary font-bold">
              {disabled ? '-' : (spec.vGpuSpec?.title ?? '-')}
            </div>
          </FieldWithTooltip>
        )}

        {/* Placeholders to maintain 3-column layout */}
        {isGPU && <EmptyField />}
        {isCPU && (
          <>
            <EmptyField />
            <EmptyField />
          </>
        )}
      </div>

      {/* Row 2: vCPU Allocation, Memory Allocation, Storage Allocation */}
      <div className="flex gap-4">
        <SimpleField label={t('instanceSpec.vcpuAllocation')}>
          {disabled ? (
            <div className="t-sb text-primary font-bold">-</div>
          ) : (
            <AllocationValue allocation={cpuAlloc} />
          )}
        </SimpleField>
        <SimpleField label={t('instanceSpec.memoryAllocation')}>
          {disabled ? (
            <div className="t-sb text-primary font-bold">-</div>
          ) : (
            <AllocationValue allocation={memAlloc} />
          )}
        </SimpleField>
        <SimpleField label={t('instanceSpec.storageAllocation')}>
          {disabled ? (
            <div className="t-sb text-primary font-bold">-</div>
          ) : (
            <AllocationValue allocation={storageAlloc} />
          )}
        </SimpleField>
      </div>
    </div>
  )
})
